/**
 * Remembers where a signed-out visitor was headed (for example a registration
 * invite) so sign-in, email verification and first-time profile completion can
 * continue there. Only same-app paths are accepted.
 */

export const POST_AUTH_REDIRECT_STORAGE_KEY = 'maktab:postAuthRedirect'
/** Query param on `/login` naming the in-app path to open after sign-in. */
export const NEXT_PARAM = 'next' as const
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

/** Same-origin absolute path only: no protocol-relative, backslash or scheme tricks. */
export function safeInternalPath(value: string | null | undefined): string | null {
  if (!value) return null
  const path = value.trim()
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(path)) return null
  return path
}

export function setPostAuthRedirect(path: string, now: number = Date.now()): void {
  const safe = safeInternalPath(path)
  if (!safe) return
  try {
    localStorage.setItem(POST_AUTH_REDIRECT_STORAGE_KEY, JSON.stringify({ path: safe, at: now }))
  } catch {
    // Storage may be unavailable (private mode); the dashboard card still links the invite.
  }
}

export function peekPostAuthRedirect(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(POST_AUTH_REDIRECT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown }
    const at = typeof parsed.at === 'number' ? parsed.at : 0
    if (now - at > MAX_AGE_MS) {
      localStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY)
      return null
    }
    return safeInternalPath(typeof parsed.path === 'string' ? parsed.path : null)
  } catch {
    return null
  }
}

export function clearPostAuthRedirect(): void {
  try {
    localStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/** `/login?next=...&email=...` for a page that requires the visitor to sign in. */
export function loginPathWithNext(next: string, email?: string | null): string {
  const qs = new URLSearchParams()
  const safe = safeInternalPath(next)
  if (safe) qs.set(NEXT_PARAM, safe)
  if (email) qs.set('email', email)
  const query = qs.toString()
  return query ? `/login?${query}` : '/login'
}
